// src/components/TimeEntry.js
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useLayoutEffect,
} from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { MsalAuthenticationTemplate } from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import styles from "./TimeEntry.module.css";
import { loginRequest, protectedResources } from "../authConfig";
import useAuthToken from "../hooks/useAuthToken";
import { useUser } from "../utils/userContext";
import { createApiClient } from "../utils/apiClient";
import { isAdmin as checkIsAdmin } from "../utils/access";
import FeatureGate from "../utils/featureGate";
import Toast from "./Toast";
import SearchableSelect from "./SearchableSelect";
import { useTier } from "../utils/tierContext";
import type { UserInfo } from "../types/UserTypes";
import type {
  TimeDetails,
} from "../types/apiSchemaAliases";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
dayjs.extend(isSameOrBefore);

type TimeDetailRow = {
  timeId?: number;
  tenantId?: string;
  userId?: number;
  projectId?: number | null;
  projectDescriptionId?: number | null;
  workDate?: string;
  hours?: number;
  description?: string | null;
  jobTypeId?: number;
  invoiceId?: number;
  hasExpenses?: boolean;
  approvalStatus?: string;
  totalSteps?: number;
  completedSteps?: number;
  needsSave?: boolean;
  [key: string]: unknown;
};

type ApprovalChainStep = {
  id: number;
  stepOrder: number;
  approverId: number;
  approverName: string;
  approverType: string;
  status: string;
  comments?: string | null;
  respondedAt?: string | null;
};

type ApprovalChainResponse = {
  timeId: number;
  totalSteps: number;
  completedSteps: number;
  overallStatus: string;
  waitingOn?: ApprovalChainStep | null;
  steps: ApprovalChainStep[];
};

type TimeDueClient = {
  clientId?: number;
  clientName?: string;
  billingPeriodMode?: string;
  periodStart?: string;
  periodEnd?: string;
};

type TimeDueMetadata = {
  dueDate?: string;
  clients?: TimeDueClient[];
};

const ADD_ENTRY_DAY_OPTIONS = [0, 0.5, 1, 1.5];
const ROW_DAY_OPTIONS = [0, 0.5, 1, 1.5, 2, 3, 4, 5];
const ADD_ENTRY_VALIDATION_ID = "timeEntryAddEntryValidation";

const createEmptyExpenseDetails = () => ({
  timeId: undefined as number | undefined,
  lineItems: [] as { id?: number; expenseTypeId: number; amount: string }[],
});

const parseExpenseAmount = (value: any) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeLegalLabel = (value: string) =>
  value.trim().replace(/\s+/g, " ").toLowerCase();

const buildStructuredLegalLabel = (l: any): string => {
  const county = String(l.countyName || "").trim();
  const parts: string[] = [];
  if (l.section) parts.push(`Sec ${String(l.section).trim()}`);
  if (l.block) parts.push(`Blk ${String(l.block).trim()}`);
  if (l.township) parts.push(`Twp ${String(l.township).trim()}`);
  if (l.range) parts.push(`Rng ${String(l.range).trim()}`);
  if (l.abstract) parts.push(`Abs ${String(l.abstract).trim()}`);
  return parts.length > 0 ? `${county}${county ? " — " : ""}${parts.join(", ")}` : county;
};

const formatLegalLabel = (l: any): string => {
  const description = String(l.description || "").trim();
  const structured = buildStructuredLegalLabel(l);
  if (!structured) return description || "—";
  if (!description) return structured;
  if (normalizeLegalLabel(description) === normalizeLegalLabel(structured)) return description;
  return `${description} — ${structured}`;
};

const normalizeDate = (value: any) => {
  const d = value instanceof Date ? value : new Date(value);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const getSemiMonthlyRangeForDate = (value: any) => {
  const d = normalizeDate(value);
  const year = d.getFullYear();
  const month = d.getMonth();

  if (d.getDate() <= 15) {
    return {
      from: new Date(year, month, 1),
      to: new Date(year, month, 15),
    };
  }

  return {
    from: new Date(year, month, 16),
    to: new Date(year, month + 1, 0),
  };
};

const shiftSemiMonthlyRange = (rangeStart: any, direction: any) => {
  if (!rangeStart) return getSemiMonthlyRangeForDate(new Date());

  const start = normalizeDate(rangeStart);
  const year = start.getFullYear();
  const month = start.getMonth();
  const isFirstHalf = start.getDate() <= 15;

  if (direction < 0) {
    if (!isFirstHalf) {
      return {
        from: new Date(year, month, 1),
        to: new Date(year, month, 15),
      };
    }

    return {
      from: new Date(year, month - 1, 16),
      to: new Date(year, month, 0),
    };
  }

  if (isFirstHalf) {
    return {
      from: new Date(year, month, 16),
      to: new Date(year, month + 1, 0),
    };
  }

  return {
    from: new Date(year, month + 1, 1),
    to: new Date(year, month + 1, 15),
  };
};

const formatRangeDisplay = (from: any, to: any) => {
  if (!from || !to) return "Select Billing Period";
  return `${dayjs(from).format("M/D/YYYY")} - ${dayjs(to).format("M/D/YYYY")}`;
};

const todayYmdLocal = () => dayjs().format("YYYY-MM-DD");

const getValidTimeDueMetadata = (state: unknown): TimeDueMetadata | null => {
  if (!state || typeof state !== "object") return null;
  const metadata = state as TimeDueMetadata;
  if (typeof metadata.dueDate !== "string" || metadata.dueDate !== todayYmdLocal()) return null;
  const clients = Array.isArray(metadata.clients)
    ? metadata.clients.filter((client) =>
        client &&
        typeof client.clientName === "string" &&
        typeof client.periodStart === "string" &&
        typeof client.periodEnd === "string"
      )
    : [];
  return clients.length ? { dueDate: metadata.dueDate, clients } : null;
};

function ArrowLeftIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M12.5 4.167L6.667 10l5.833 5.833"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M7.5 4.167L13.333 10 7.5 15.833"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ReceiptIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M6.667 2.5v2.083M10 2.5v2.083M13.333 2.5v2.083M5 4.583h10a1.667 1.667 0 0 1 1.667 1.667v9.583l-1.667-.833-1.667.833-1.666-.833-1.667.833-1.667-.833-1.666.833-1.667-.833-1.667.833V6.25A1.667 1.667 0 0 1 5 4.583Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7.083 8.333h5.834M7.083 11.25h4.167"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M10 4.167v11.666M4.167 10h11.666"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function SaveIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M4.167 4.167h9.166l2.5 2.5v8.75A1.25 1.25 0 0 1 14.583 16.667H5.417a1.25 1.25 0 0 1-1.25-1.25V5.417a1.25 1.25 0 0 1 1.25-1.25Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M6.667 4.167v4.166h5V4.167M6.667 16.667v-4.584h6.666v4.584"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M11.667 3.333l2.5 2.5M3.333 13.333l-0.833 4.167 4.167-0.833L15.833 7.5a1.768 1.768 0 0 0 0-2.5l0-0a1.768 1.768 0 0 0-2.5 0L4.167 14.167Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M5.833 6.667v8.75A1.25 1.25 0 0 0 7.083 16.667h5.834a1.25 1.25 0 0 0 1.25-1.25v-8.75M4.167 5h11.666M7.5 5V3.75c0-.69.56-1.25 1.25-1.25h2.5c.69 0 1.25.56 1.25 1.25V5M8.333 9.167v4.166M11.667 9.167v4.166"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TimeEntryDateRangeField({
  rangeStart,
  rangeEnd,
  onChange,
  disabled = false,
}: any) {
  const triggerRef = useRef<any>(null);
  const popoverRef = useRef<any>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false,
  );
  const [visibleMonth, setVisibleMonth] = useState(rangeStart || new Date());
  const [popoverPos, setPopoverPos] = useState({
    top: 0,
    left: 0,
    width: 340,
    ready: false,
  });

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setVisibleMonth(rangeStart || new Date());
    }
  }, [rangeStart, isOpen]);

  const updatePopoverPosition = () => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const left = Math.max(12, rect.left - 50);
    const top = rect.bottom + 4;

    setPopoverPos({
      top,
      left,
      width: 0,
      ready: true,
    });
  };

  useLayoutEffect(() => {
    if (!isOpen || isMobile) return;

    updatePopoverPosition();

    const handleReposition = () => updatePopoverPosition();

    window.addEventListener("resize", handleReposition);
    document.addEventListener("scroll", handleReposition, true);

    return () => {
      window.removeEventListener("resize", handleReposition);
      document.removeEventListener("scroll", handleReposition, true);
    };
  }, [isOpen, isMobile]);

  useEffect(() => {
    if (!isOpen) return;

    const handleOutside = (event: any) => {
      const target = event.target;

      if (triggerRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;

      setIsOpen(false);
    };

    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [isOpen]);

  const openPicker = () => {
    if (disabled) return;

    setVisibleMonth(rangeStart || new Date());

    if (!isMobile) {
      setPopoverPos((prev) => ({ ...prev, ready: false }));
      updatePopoverPosition();
    } else {
      setPopoverPos((prev) => ({ ...prev, ready: true }));
    }

    setIsOpen(true);
  };

  const handleDayClick = (day: any, modifiers: any) => {
    if (disabled || modifiers.disabled) return;

    const nextRange = getSemiMonthlyRangeForDate(day);
    onChange(nextRange);
    setVisibleMonth(nextRange.from);
    setIsOpen(false);
  };

  const calendar = (
    <div
      ref={popoverRef}
      className={`${styles['te-rdp-wrap']} ${isMobile ? styles['mobile-calendar-card'] : styles['calendar-card']}`}
      style={
        isMobile
          ? undefined
          : {
              top: popoverPos.top,
              left: popoverPos.left,
              visibility: popoverPos.ready ? "visible" : "hidden",
            }
      }
    >
      {isMobile && (
        <div className={styles['mobile-calendar-header']}>
          <div className={styles['mobile-calendar-title']}>
            Select Billing Period
          </div>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className={styles['mobile-calendar-close']}
          >
            Close
          </button>
        </div>
      )}

      <div className={styles['calendar-hint']}>
        Pick any date to load the first or second half of that month.
      </div>

      <DayPicker
        mode="range"
        numberOfMonths={1}
        selected={{ from: rangeStart, to: rangeEnd }}
        month={visibleMonth}
        onMonthChange={setVisibleMonth}
        onDayClick={handleDayClick}
        showOutsideDays
      />
    </div>
  );

  return (
    <>
      <button
        id="reportRange"
        ref={triggerRef}
        type="button"
        className={`datePick2 ${styles['time-entry-range-trigger']} ${styles['trigger-button']}`}
        disabled={disabled}
        onClick={openPicker}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label="Select billing period"
      >
        <span className={styles['trigger-text']}>
          {formatRangeDisplay(rangeStart, rangeEnd)}
        </span>
      </button>

      {isOpen &&
        typeof document !== "undefined" &&
        createPortal(
          isMobile ? (
            <div className={styles['mobile-calendar-backdrop']}>{calendar}</div>
          ) : (
            calendar
          ),
          document.body,
        )}
    </>
  );
}

const TimeEntry = () => {
  const authRequest = { ...loginRequest };
  const defaultRange = useMemo(
    () => getSemiMonthlyRangeForDate(new Date()),
    [],
  );

  const location = useLocation();
  const nav = useNavigate();
  const { user, loading, activeTenantId } = useUser();
  const { token, getToken } = useAuthToken();

  const api = useMemo(() => {
    if (!token || !activeTenantId || !user?.userId) return null;
    return createApiClient({
      baseUrl: protectedResources.baseURL.endpoint,
      token,
      getToken,
      tenantId: activeTenantId,
      userId: user.userId,
    });
  }, [token, getToken, activeTenantId, user?.userId]);

  const isAdmin = checkIsAdmin(Number(user?.roleId));
  const { hasFeature, isVisible } = useTier();
  const hasExpenseAccounts = hasFeature("company_expense_accounts");
  const hasExpenseEntry = hasFeature("expense_entry");
  const hasAdminOverride = hasFeature("time_overrides");
  const hasApprovals = hasFeature("approval_workflow");
  const showClient = isVisible("timeentry_show_client");
  const showProspect = isVisible("timeentry_show_prospect");
  const showLegal = isVisible("timeentry_show_legal");
  const showJob = isVisible("timeentry_show_job");
  const showDescription = isVisible("timeentry_show_description");

  const [entryMode, setEntryMode] = useState("self"); // "self" | "user" | "expense"
  const [activeUsers, setActiveUsers] = useState<UserInfo[]>([]);
  const [expenseAccounts, setExpenseAccounts] = useState<UserInfo[]>([]);
  const [selectedEntryUserId, setSelectedEntryUserId] = useState("");
  const [selectedExpenseAccountId, setSelectedExpenseAccountId] = useState("");

  const [selectedDate, setSelectedDate] = useState("");
  const [selectedProspect, setSelectedProspect] = useState("");
  const [selectedLegal, setSelectedLegal] = useState("");
  const [selectedJob, setSelectedJob] = useState("");
  const [selectedDays, setSelectedDays] = useState("");
  const [selectedDescription, setSelectedDescription] = useState("");
  const [prospects, setProspects] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [legals, setLegals] = useState<any[]>([]);
  const [legalsLoading, setLegalsLoading] = useState(false);
  const [jobs, setJobs] = useState<any[]>([]);

  const [rangeStart, setRangeStart] = useState(defaultRange.from);
  const [rangeEnd, setRangeEnd] = useState(defaultRange.to);
  const [dateOptions, setDateOptions] = useState<any[]>([]);

  const [timeDetails, setTimeDetails] = useState<TimeDetailRow[]>([]);
  const [timeDetailsLoaded, setTimeDetailsLoaded] = useState(false);
  const [legalsByProject, setLegalsByProject] = useState<any>({});

  const [savingRows, setSavingRows] = useState<any>({});
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);
  const [activeExpenseTime, setActiveExpenseTime] = useState<any>(null);
  const [expenseOpenKey, setExpenseOpenKey] = useState(0);
  const [approvalChains, setApprovalChains] = useState<Record<number, ApprovalChainResponse>>({});

  const [expenseDetails, setExpenseDetails] = useState(
    createEmptyExpenseDetails,
  );
  const [expenseTypes, setExpenseTypes] = useState<any[]>([]);
  const [receiptDetails, setReceiptDetails] = useState<any[]>([]);
  const [pendingReceipts, setPendingReceipts] = useState<any[]>([]);
  const [disabledExpenses, setDisabledExpenses] = useState(false);
  const [isReceiptDragActive, setIsReceiptDragActive] = useState(false);
  const [rowsWithExpenses, setRowsWithExpenses] = useState<Set<number>>(new Set());
  const [timeDueMetadata, setTimeDueMetadata] = useState<TimeDueMetadata | null>(null);
  const [timeDueBannerDismissed, setTimeDueBannerDismissed] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const [highlightTimeId, setHighlightTimeId] = useState<number | null>(null);
  const [showAddEntryValidation, setShowAddEntryValidation] = useState(false);

  // Read navigation state from notification click-through
  useEffect(() => {
    const state = location.state as ({ timeId?: number; workDate?: string } & TimeDueMetadata) | null;
    const validTimeDueMetadata = getValidTimeDueMetadata(state);
    if (validTimeDueMetadata) {
      setTimeDueMetadata(validTimeDueMetadata);
      setTimeDueBannerDismissed(false);
    }
    if (!state) return;
    if (!state.timeId && !state.workDate && !validTimeDueMetadata) return;

    if (state.workDate) {
      // Parse as local date (avoid UTC offset shifting the day)
      const [y, m, d] = state.workDate.split("-").map(Number);
      const range = getSemiMonthlyRangeForDate(new Date(y, m - 1, d));
      setRangeStart(range.from);
      setRangeEnd(range.to);
    }

    if (state.timeId) {
      setHighlightTimeId(state.timeId);
    }

    // Clear state so refreshes / back-nav don't re-trigger
    nav(location.pathname, { replace: true, state: null });
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to and auto-clear highlighted row after data loads
  useEffect(() => {
    if (!highlightTimeId || !timeDetailsLoaded) return;

    const scrollTimer = setTimeout(() => {
      const el = document.querySelector(`[data-testid="time-entry-${highlightTimeId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 300);

    const clearTimer = setTimeout(() => {
      setHighlightTimeId(null);
    }, 3500);

    return () => {
      clearTimeout(scrollTimer);
      clearTimeout(clearTimer);
    };
  }, [highlightTimeId, timeDetailsLoaded]);

  const fileInputRef= useRef<any>(null);
  const receiptDragCounterRef = useRef(0);

  const formatApiDate = (m: any) => dayjs(m).format("YYYY-MM-DD");
  const asNum = (v: any) => (v == null || v === "" ? null : Number(v));

  const getStandardUserDisplayName = (u: any) => {
    if (!u) return "";
    const fullName = `${u.firstName || ""} ${u.lastName || ""}`.trim();
    if (fullName) return fullName;
    return `User ${u.userId}`;
  };

  const getExpenseAccountDisplayName = (u: any) => {
    if (!u) return "";
    const accountName = (u.businessName || "").trim();
    if (accountName) return accountName;
    return `Expense Account ${u.userId}`;
  };

  const buildExpensePayload = (overrides: any = {}) => {
    const source = { ...expenseDetails, ...overrides };

    const lineItems = (source.lineItems || []).map((li: any) => ({
      id: li.id,
      expenseTypeId: li.expenseTypeId,
      amount: parseExpenseAmount(li.amount),
    }));

    return {
      timeId:
        source.timeId == null || source.timeId === ""
          ? undefined
          : Number(source.timeId),
      lineItems,
    };
  };

  const handleExpenseAmountChange = (expenseTypeId: number, rawValue: string) => {
    if (disabledExpenses) return;

    const et = expenseTypes.find((t: any) => t.id === expenseTypeId);
    const isInteger = et?.inputType === "integer";

    const valid = isInteger
      ? rawValue === "" || /^\d+$/.test(rawValue)
      : rawValue === "" || /^\d*\.?\d{0,2}$/.test(rawValue);

    if (valid) {
      setExpenseDetails((prev) => ({
        ...prev,
        lineItems: prev.lineItems.map((li) =>
          li.expenseTypeId === expenseTypeId ? { ...li, amount: rawValue } : li
        ),
      }));
    }
  };

  const effectiveUserId = useMemo(() => {
    if (!user?.userId) return null;

    if (!isAdmin || entryMode === "self") return Number(user.userId);
    if (entryMode === "user") {
      return selectedEntryUserId ? Number(selectedEntryUserId) : null;
    }
    if (entryMode === "expense") {
      return selectedExpenseAccountId ? Number(selectedExpenseAccountId) : null;
    }

    return Number(user.userId);
  }, [
    isAdmin,
    entryMode,
    selectedEntryUserId,
    selectedExpenseAccountId,
    user?.userId,
  ]);

  const effectiveContextLabel = useMemo(() => {
    if (!user?.userId) return "";

    if (!isAdmin || entryMode === "self") {
      return getStandardUserDisplayName(user);
    }

    if (entryMode === "user") {
      const found = activeUsers.find(
        (x) => Number(x.userId) === Number(selectedEntryUserId),
      );
      return found ? getStandardUserDisplayName(found) : "";
    }

    if (entryMode === "expense") {
      const found = expenseAccounts.find(
        (x) => Number(x.userId) === Number(selectedExpenseAccountId),
      );
      return found ? getExpenseAccountDisplayName(found) : "";
    }

    return "";
  }, [
    isAdmin,
    entryMode,
    user,
    activeUsers,
    expenseAccounts,
    selectedEntryUserId,
    selectedExpenseAccountId,
  ]);

  const missingAddEntryFields = useMemo(() => {
    const missing: { key: string; label: string; controlId: string }[] = [];

    if (!selectedDate) {
      missing.push({ key: "date", label: "Date", controlId: "dateRangePicker" });
    }

    if (showClient && !selectedClient) {
      missing.push({ key: "client", label: "Client", controlId: "entryClient" });
    } else if (showProspect && !selectedProspect) {
      missing.push({ key: "prospect", label: "Prospect", controlId: "entryProspect" });
    } else if (showLegal && !selectedLegal) {
      missing.push({ key: "legal", label: "Legal", controlId: "entryLegal" });
    }

    if (showJob && !selectedJob) {
      missing.push({ key: "job", label: "Job", controlId: "entryJob" });
    }

    if (selectedDays === "") {
      missing.push({ key: "days", label: "Days", controlId: "entryDays" });
    }

    return missing;
  }, [
    selectedDate,
    selectedClient,
    selectedProspect,
    selectedLegal,
    selectedJob,
    selectedDays,
    showClient,
    showProspect,
    showLegal,
    showJob,
  ]);

  const missingAddEntryFieldKeys = useMemo(
    () => new Set(missingAddEntryFields.map((field) => field.key)),
    [missingAddEntryFields],
  );

  const isAddEntryFieldMissing = (key: string) =>
    showAddEntryValidation && missingAddEntryFieldKeys.has(key);

  useEffect(() => {
    if (showAddEntryValidation && missingAddEntryFields.length === 0) {
      setShowAddEntryValidation(false);
    }
  }, [showAddEntryValidation, missingAddEntryFields.length]);

  const resetEntryInputs = useCallback(() => {
    setSelectedDate("");
    setSelectedClient("");
    setSelectedProspect("");
    setSelectedLegal("");
    setSelectedJob("");
    setSelectedDays("");
    setSelectedDescription("");
    setProspects([]);
    setLegals([]);
    setLegalsByProject({});
    setTimeDetails([]);
    setTimeDetailsLoaded(false);
  }, []);

  const resetExpenseDetailsInput = useCallback(() => {
    setExpenseDetails(createEmptyExpenseDetails());
    setReceiptDetails([]);
    setPendingReceipts([]);
    setIsReceiptDragActive(false);
    receiptDragCounterRef.current = 0;

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const createPendingReceipt = (file: any) => ({
    pendingId: `${file.name}-${file.size}-${file.lastModified}-${Math.random()
      .toString(36)
      .slice(2)}`,
    name: file.name,
    file,
  });

  const appendPendingReceipts = (filesLike: any) => {
    const files = Array.from(filesLike || []);
    if (!files.length || disabledExpenses) return;

    const additions = files.map(createPendingReceipt);
    setPendingReceipts((prev) => [...prev, ...additions]);
  };

  const loadActiveUsers = useCallback(async () => {
    if (!api) return [];
    try {
      const data = await api.get("userinfo/lookup/users?includeCompanyExpense=false");
      const list = Array.isArray(data) ? data : [];

      const sorted = list
        .sort((a, b) =>
          getStandardUserDisplayName(a)
            .toLowerCase()
            .localeCompare(getStandardUserDisplayName(b).toLowerCase()),
        );

      setActiveUsers(sorted);
      return sorted;
    } catch (err: any) {
      if (err?.status === 404) { setActiveUsers([]); return []; }
      console.error("❌ Error loading active users:", err);
      setToast({ message: "Failed to load users.", type: "error" });
      setActiveUsers([]);
      return [];
    }
  }, [api]);

  const loadExpenseAccounts = useCallback(async () => {
    if (!api || !hasExpenseAccounts) { setExpenseAccounts([]); return []; }
    try {
      const data = await api.get("companyexpenseaccounts?includeInactive=false");
      const list = Array.isArray(data) ? data : [];

      const filtered = list
        .filter((u) => u.isActive !== false)
        .sort((a, b) =>
          getExpenseAccountDisplayName(a)
            .toLowerCase()
            .localeCompare(getExpenseAccountDisplayName(b).toLowerCase()),
        );

      setExpenseAccounts(filtered);
      return filtered;
    } catch (err: any) {
      if (err?.status === 404 || err?.status === 403) { setExpenseAccounts([]); return []; }
      console.error("❌ Error loading expense accounts:", err);
      setToast({ message: "Failed to load expense accounts.", type: "error" });
      setExpenseAccounts([]);
      return [];
    }
  }, [api, hasExpenseAccounts]);

  const fetchLegals = async (projectId: any) => {
    if (!api || !projectId) return [];
    const data = await api.get(`projectdescriptions/${projectId}`);
    return Array.isArray(data) ? data : [];
  };

  const loadLegals = async (projectId: any) => {
    if (!projectId) {
      setLegals([]);
      return;
    }
    setLegalsLoading(true);
    try {
      const data = await fetchLegals(projectId);
      setLegals(data);
      if (data.length > 0) setSelectedLegal(String(data[0].id));
      else setSelectedLegal("");
    } catch (e) {
      console.error("❌ Error loading legals:", e);
      setLegals([]);
      setSelectedLegal("");
    } finally {
      setLegalsLoading(false);
    }
  };

  const ensureLegalsForProject = async (projectId: any) => {
    const id = Number(projectId);
    if (!api || !id) return [];
    if (legalsByProject[id]) return legalsByProject[id];

    try {
      const data = await fetchLegals(id);
      setLegalsByProject((p: any) => ({ ...p, [id]: data }));
      return data;
    } catch (e) {
      console.error("❌ Error caching legals:", e);
      setLegalsByProject((p: any) => ({ ...p, [id]: [] }));
      return [];
    }
  };

  const loadProspects = useCallback(async () => {
    if (!api || !effectiveUserId) {
      setProspects([]);
      return;
    }

    try {
      const path = entryMode === "self"
        ? "assigneduserstoprojects/Projects"
        : `userinfo/${effectiveUserId}/assigned-projects`;
      const data = await api.get(path);
      setProspects(Array.isArray(data) ? data : []);
    } catch (err: any) {
      if (err?.status === 404) { setProspects([]); return; }
      console.error("❌ Error loading prospects:", err);
      setToast({ message: "Failed to load prospects.", type: "error" });
      setProspects([]);
    }
  }, [api, effectiveUserId, entryMode]);

  const loadClients = useCallback(async () => {
    if (!api || !effectiveUserId) return;
    try {
      const path = entryMode === "self"
        ? "assigneduserstoprojects/Clients"
        : `userinfo/${effectiveUserId}/assigned-clients`;
      const data = await api.get(path);
      const list = Array.isArray(data) ? data : [];
      list.sort((a: any, b: any) =>
        (a.businessName || "").toLowerCase().localeCompare((b.businessName || "").toLowerCase())
      );
      setClients(list);
    } catch (err) {
      console.error("❌ Error loading clients:", err);
      setClients([]);
    }
  }, [api, effectiveUserId, entryMode]);

  const loadJobs= useCallback(async () => {
    if (!api) return;
    try {
      const data = await api.get("jobtypes/");
      setJobs(Array.isArray(data) ? data : []);
    } catch (err: any) {
      if (err?.status === 404) { setJobs([]); return; }
      console.error("❌ Error loading jobs:", err);
      setToast({ message: "Failed to load jobs.", type: "error" });
      setJobs([]);
    }
  }, [api]);

  const loadExpenseTypes = useCallback(async () => {
    if (!api) return;
    try {
      const data = await api.get("expensetypes");
      const list = Array.isArray(data) ? data : [];
      setExpenseTypes(list.filter((t: any) => t.isActive).sort((a: any, b: any) => a.sortOrder - b.sortOrder));
    } catch (err) {
      console.error("❌ Error loading expense types:", err);
      setExpenseTypes([]);
    }
  }, [api]);

  const loadTimeDetails= useCallback(async () => {
    if (!api || !effectiveUserId || !rangeStart || !rangeEnd) {
      setTimeDetails([]);
      setTimeDetailsLoaded(false);
      return;
    }

    setTimeDetailsLoaded(false);
    setTimeDetails([]);
    try {
      const start = formatApiDate(rangeStart);
      const end = formatApiDate(rangeEnd);

      const path = entryMode === "self"
        ? `timedetails/${start}/${end}`
        : `timedetails/${effectiveUserId}/${start}/${end}`;

      const data = await api.get(
        path,
        { query: { _ts: String(Date.now()) } },
      );

      const list: TimeDetailRow[] = (Array.isArray(data) ? data : []).map((d: any) => ({
        ...d,
        totalSteps: d.approvals?.totalSteps ?? 0,
        completedSteps: d.approvals?.completedSteps ?? 0,
      }));
      list.sort((a, b) => new Date(a.workDate!).getTime() - new Date(b.workDate!).getTime());

      setTimeDetails(list);

      const ids = Array.from(
        new Set(list.map((r) => Number(r.projectId)).filter(Boolean)),
      );
      await Promise.all(ids.map((id) => ensureLegalsForProject(id)));

      setTimeDetailsLoaded(true);

      // Build expense set from hasExpenses flag returned by API
      const expenseSet = new Set<number>();
      for (const row of list) {
        if (row.hasExpenses) expenseSet.add(Number(row.timeId));
      }
      setRowsWithExpenses(expenseSet);
    } catch (err: any) {
      // 404 means no records for this period — treat as empty, not an error
      if (err?.status === 404) {
        setTimeDetails([]);
        setTimeDetailsLoaded(true);
        return;
      }
      console.error("❌ Error loading time details:", err);
      setToast({ message: "Failed to load time entries.", type: "error" });
      setTimeDetails([]);
      setTimeDetailsLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ensureLegalsForProject is not memoized; adding it would defeat useCallback memoization
  }, [api, effectiveUserId, entryMode, rangeStart, rangeEnd]);

  const handleTimeDetailChange = (timeId: any, field: any, value: any) => {
    setTimeDetails((prev) =>
      prev.map((d) =>
        d.timeId === timeId ? { ...d, [field]: value, needsSave: true } : d,
      ),
    );
  };

  const handleRowProspectChange = async (
    timeId: any,
    newProjectId: any,
    { userInitiated = false } = {},
  ) => {
    const pid = asNum(newProjectId);
    const list = await ensureLegalsForProject(pid);

    setTimeDetails((prev) =>
      prev.map((d) => {
        if (d.timeId !== timeId) return d;

        const currentLegal = asNum(d.projectDescriptionId);
        const legalExists = list?.some((l: any) => asNum(l.id) === currentLegal);

        const nextLegal = legalExists ? currentLegal : asNum(list?.[0]?.id);

        return {
          ...d,
          projectId: pid,
          projectDescriptionId: nextLegal ?? d.projectDescriptionId,
          needsSave: userInitiated ? true : d.needsSave,
        };
      }),
    );
  };

  const saveTimeDetail = async (detail: any) => {
    const timeId = Number(detail.timeId);
    try {
      if (!api || !effectiveUserId) return;
      setSavingRows((prev: any) => ({ ...prev, [timeId]: true }));

      const payload = {
        timeId,
        userId: Number(effectiveUserId),
        projectId: Number(detail.projectId),
        projectDescriptionId: Number(detail.projectDescriptionId),
        jobTypeId: Number(detail.jobTypeId ?? detail.jobTypeID),
        hours: Number(detail.hours),
        invoiceId: Number(detail.invoiceId ?? 0),
        description: detail.description ?? "",
        workDate: dayjs(detail.workDate).format("YYYY-MM-DD"),
      };

      await api.put(`timedetails/${timeId}`, payload);

      setTimeDetails((prev) =>
        prev.map((d) => (d.timeId === timeId ? { ...d, needsSave: false, approvalStatus: 'pending', completedSteps: 0 } : d)),
      );
      setApprovalChains(prev => {
        const copy = { ...prev };
        delete copy[timeId];
        return copy;
      });
      // Re-fetch approval chain to get fresh totalSteps/completedSteps
      try {
        const chain = await api.get(`approvals/chain/${timeId}`) as ApprovalChainResponse;
        if (chain && chain.steps) {
          setApprovalChains(prev => ({ ...prev, [timeId]: chain }));
          setTimeDetails(prev =>
            prev.map(d => d.timeId === timeId ? { ...d, totalSteps: chain.totalSteps, completedSteps: chain.completedSteps, approvalStatus: chain.overallStatus } : d)
          );
        }
      } catch (_) { /* non-critical */ }
      setToast({ message: "Entry saved.", type: "success" });
    } catch (err) {
      console.error("❌ Error saving time detail:", err);
      setToast({ message: "Failed to save entry.", type: "error" });
    } finally {
      setSavingRows((prev: any) => {
        const copy = { ...prev };
        delete copy[timeId];
        return copy;
      });
    }
  };

  const deleteTimeDetail = async (timeId: any) => {
    const id = Number(timeId);
    try {
      if (!api) return;
      await api.delete(`timedetails/${id}`);
      setTimeDetails((prev) => prev.filter((d) => d.timeId !== id));
      setToast({ message: "Entry deleted.", type: "success" });
    } catch (err) {
      console.error("❌ Error deleting time detail:", err);
      setToast({ message: "Failed to delete entry.", type: "error" });
    }
  };

  const getReceiptDetails = async (timeIdParam: any) => {
    const timeId = timeIdParam ?? activeExpenseTime?.timeId;
    if (!api || !timeId) {
      setReceiptDetails([]);
      return;
    }

    try {
      const data = await api.get(`receipts/${timeId}`);
      setReceiptDetails(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("❌ Error loading receipts:", err);
      setToast({ message: "Failed to load receipts.", type: "error" });
      setReceiptDetails([]);
    }
  };

  const fetchApprovalChain = async (timeId: number) => {
    if (!api || !timeId) return;
    try {
      const data = await api.get(`approvals/chain/${timeId}`) as ApprovalChainResponse;
      if (data && data.steps) {
        setApprovalChains(prev => ({ ...prev, [timeId]: data }));
        // Sync row-level approval counts with the fresh chain data
        setTimeDetails(prev =>
          prev.map(d =>
            d.timeId === timeId
              ? { ...d, totalSteps: data.totalSteps, completedSteps: data.completedSteps, approvalStatus: data.overallStatus }
              : d
          )
        );
      }
    } catch (err: any) {
      console.error("❌ Error loading approval chain:", err);
      setToast({ message: err?.message || "Failed to load approval chain.", type: "error" });
    }
  };

  const getExpenseDetails = async (detail: any) => {
    resetExpenseDetailsInput();
    setActiveExpenseTime(detail);
    setExpenseOpenKey(k => k + 1);

    if (!api) return;

    if (detail == null) {
      setDisabledExpenses(false);
      setExpenseDetails({
        timeId: undefined,
        lineItems: expenseTypes.map((t: any) => ({ expenseTypeId: t.id, amount: "0" })),
      });
      return;
    }

    setDisabledExpenses(Number(detail.invoiceId) > 0);

    try {
      const data = await api.get(`expenses/${detail.timeId}`);
      if (Array.isArray(data) && data.length > 0) {
        // API returns flat array of line items: [{id, timeId, expenseTypeId, amount}]
        const existingMap = new Map<number, any>(data.map((li: any) => [li.expenseTypeId, li]));
        const items = expenseTypes.map((t: any) => {
          const existing = existingMap.get(t.id);
          return {
            id: existing?.id,
            expenseTypeId: t.id,
            amount: existing ? String(existing.amount) : "0",
          };
        });

        setExpenseDetails({
          timeId: detail.timeId,
          lineItems: items,
        });
      } else {
        setExpenseDetails({
          timeId: detail.timeId,
          lineItems: expenseTypes.map((t: any) => ({ expenseTypeId: t.id, amount: "0" })),
        });
      }

      await getReceiptDetails(detail.timeId);
    } catch (err) {
      console.error("❌ Error loading expense details:", err);
      setToast({ message: "Failed to load expense details.", type: "error" });
    }
  };

  const saveExpenseDetailsInput = async () => {
    if (!api || !expenseDetails.timeId) return;
    const timeId = expenseDetails.timeId;

    // Build array payload: only include items with amount > 0
    const payload = expenseDetails.lineItems
      .map(li => ({ expenseTypeId: li.expenseTypeId, amount: parseExpenseAmount(li.amount) }))
      .filter(li => li.amount > 0);

    try {
      const hasExisting = expenseDetails.lineItems.some(li => li.id);

      if (hasExisting) {
        // Replace all expenses for this time entry
        await api.put(`expenses/${timeId}`, payload);
      } else if (payload.length > 0) {
        // Create new expenses
        const created = await api.post(`expenses/${timeId}`, payload) as any[];
        if (Array.isArray(created)) {
          const idMap = new Map<number, number>(created.map((c: any) => [c.expenseTypeId, c.id]));
          setExpenseDetails(prev => ({
            ...prev,
            lineItems: prev.lineItems.map(li => ({
              ...li,
              id: idMap.get(li.expenseTypeId) ?? li.id,
            })),
          }));
        }
      }

      const total = payload.reduce((sum, li) => sum + li.amount, 0);
      if (total > 0) {
        setRowsWithExpenses(prev => new Set(prev).add(timeId));
      } else {
        setRowsWithExpenses(prev => { const s = new Set(prev); s.delete(timeId); return s; });
      }
    } catch (err) {
      console.error("❌ Error saving expenses:", err);
      setToast({ message: "Failed to save expense.", type: "error" });
    }
  };

  const addReceipt = (e: any) => {
    appendPendingReceipts(e.target.files);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleReceiptDragEnter = (e: any) => {
    if (disabledExpenses) return;
    e.preventDefault();
    e.stopPropagation();

    receiptDragCounterRef.current += 1;
    setIsReceiptDragActive(true);
  };

  const handleReceiptDragOver = (e: any) => {
    if (disabledExpenses) return;
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = "copy";
    }
  };

  const handleReceiptDragLeave = (e: any) => {
    if (disabledExpenses) return;
    e.preventDefault();
    e.stopPropagation();

    receiptDragCounterRef.current = Math.max(
      0,
      receiptDragCounterRef.current - 1,
    );

    if (receiptDragCounterRef.current === 0) {
      setIsReceiptDragActive(false);
    }
  };

  const handleReceiptDrop = (e: any) => {
    if (disabledExpenses) return;
    e.preventDefault();
    e.stopPropagation();

    receiptDragCounterRef.current = 0;
    setIsReceiptDragActive(false);

    appendPendingReceipts(e.dataTransfer?.files);
  };

  const triggerReceiptBrowse = () => {
    if (disabledExpenses) return;
    fileInputRef.current?.click();
  };

  const handleReceiptZoneKeyDown = (e: any) => {
    if (disabledExpenses) return;

    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      triggerReceiptBrowse();
    }
  };

  const removeReceipt = async (receipt: any) => {
    if (!api) return;

    if (!receipt.receiptId) {
      setPendingReceipts((prev) =>
        prev.filter((r) => r.pendingId !== receipt.pendingId),
      );
      return;
    }

    const id = Number(receipt.receiptId);
    const currentTimeId = activeExpenseTime?.timeId || expenseDetails?.timeId;

    try {
      setReceiptDetails((prev) => prev.filter((r) => r.receiptId !== id));
      await api.delete(`receipts/${id}`);

      if (currentTimeId) {
        await getReceiptDetails(currentTimeId);
      }
    } catch (err) {
      setReceiptDetails((prev) =>
        [...prev, receipt].sort((a, b) => a.receiptId - b.receiptId),
      );
    }
  };

  const uploadReceipts = async () => {
    if (!api) return;

    const timeId = Number(expenseDetails.timeId);
    if (!timeId || pendingReceipts.length === 0) return;

    try {
      const formData = new FormData();
      pendingReceipts.forEach((r) => formData.append("files", r.file, r.name));

      await api.postForm(`receipts/upload/${timeId}`, formData);

      setPendingReceipts([]);
      await getReceiptDetails(timeId);
      setToast({ message: "Receipts uploaded.", type: "success" });
    } catch (err) {
      console.error("❌ Error uploading receipts:", err);
      setToast({ message: "Failed to upload receipts.", type: "error" });
    }
  };

  const addEntry = async () => {
    if (missingAddEntryFields.length > 0) {
      setShowAddEntryValidation(true);
      window.setTimeout(() => {
        document.getElementById(missingAddEntryFields[0].controlId)?.focus();
      }, 0);
      return;
    }
    if (!api || !effectiveUserId) return;

    const payload = {
      description: selectedDescription,
      userId: Number(effectiveUserId),
      projectId: Number(selectedProspect),
      projectDescriptionId: Number(selectedLegal),
      jobTypeId: Number(selectedJob),
      hours: Number(selectedDays),
      invoiceId: 0,
      workDate: dayjs(selectedDate).format("YYYY-MM-DD"),
    };

    const hasExpenseValues = (expenseDetails.lineItems || []).some(
      (li) => parseExpenseAmount(li.amount) > 0
    );

    try {
      const created = await api.post<TimeDetails>("timedetails", payload);
      const newTimeId = created?.timeId;

      if (newTimeId) {
        if (hasExpenseValues) {
          const expensePayload = buildExpensePayload({ timeId: newTimeId });
          const expenseItems = expensePayload.lineItems
            .filter((li: any) => li.amount > 0)
            .map((li: any) => ({ expenseTypeId: li.expenseTypeId, amount: li.amount }));
          if (expenseItems.length > 0) {
            try {
              await api.post(`expenses/${newTimeId}`, expenseItems);
            } catch (e) {
              console.error("❌ posting expenses:", e);
            }
          }
        }

        if (pendingReceipts.length > 0) {
          const fd = new FormData();
          pendingReceipts.forEach((r) => fd.append("files", r.file, r.name));
          try {
            await api.postForm(`receipts/upload/${newTimeId}`, fd);
          } catch (e) {
            console.error("❌ uploading receipts:", e);
          }
        }
      } else {
        console.warn(
          "⚠ The POST /timedetails response didn't include timeId; can't post expenses/receipts inline.",
        );
      }

      await loadTimeDetails();
      setSelectedDate("");
      setShowAddEntryValidation(false);

      resetExpenseDetailsInput();
      setActiveExpenseTime(null);
      setToast({ message: "Entry added.", type: "success" });
    } catch (err) {
      console.error("❌ Error adding time detail:", err);
      setToast({ message: "Failed to add time entry.", type: "error" });
    }
  };

  const shiftDisplayedPeriod = (direction: any) => {
    const next = shiftSemiMonthlyRange(rangeStart, direction);
    setRangeStart(next.from);
    setRangeEnd(next.to);
  };

  const handleButtonPointerRelease = (e: any) => {
    if (typeof e.currentTarget?.blur === "function") {
      e.currentTarget.blur();
    }
  };

  useEffect(() => {
    if (!rangeStart || !rangeEnd) {
      setDateOptions([]);
      setSelectedDate("");
      return;
    }

    const start = dayjs(rangeStart).startOf("day");
    const end = dayjs(rangeEnd).startOf("day");

    const days: any[] = [];
    let cur = start;

    while (cur.isSameOrBefore(end, "day")) {
      days.push(cur.format("YYYY-MM-DD"));
      cur = cur.add(1, "day");
    }

    setDateOptions(days);
    setSelectedDate((prev) => (prev && days.includes(prev) ? prev : ""));
  }, [rangeStart, rangeEnd]);

  useEffect(() => {
    if (!api || loading) return;

    loadJobs();
    loadExpenseTypes();

    if (isAdmin) {
      (async () => {
        const [usersList, expenseList] = await Promise.all([
          loadActiveUsers(),
          loadExpenseAccounts(),
        ]);

        if (!selectedEntryUserId && usersList.length) {
          setSelectedEntryUserId(String(usersList[0].userId));
        }

        if (!selectedExpenseAccountId && expenseList.length) {
          setSelectedExpenseAccountId(String(expenseList[0].userId));
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedEntryUserId and selectedExpenseAccountId are set by this effect; adding them as deps would re-trigger on every selection change
  }, [api, loading, isAdmin, loadJobs, loadExpenseTypes, loadActiveUsers, loadExpenseAccounts]);

  useEffect(() => {
    if (!isAdmin) return;

    // Reset to "user" mode if expense accounts feature was disabled
    if (entryMode === "expense" && !hasExpenseAccounts) {
      setEntryMode("self");
      return;
    }

    if (entryMode === "user" && !hasAdminOverride) {
      setEntryMode("self");
      return;
    }

    if (entryMode === "user") {
      if (
        selectedEntryUserId &&
        !activeUsers.some(
          (x) => Number(x.userId) === Number(selectedEntryUserId),
        )
      ) {
        setSelectedEntryUserId(
          activeUsers.length ? String(activeUsers[0].userId) : "",
        );
      } else if (!selectedEntryUserId && activeUsers.length) {
        setSelectedEntryUserId(String(activeUsers[0].userId));
      }
    }

    if (entryMode === "expense") {
      if (
        selectedExpenseAccountId &&
        !expenseAccounts.some(
          (x) => Number(x.userId) === Number(selectedExpenseAccountId),
        )
      ) {
        setSelectedExpenseAccountId(
          expenseAccounts.length ? String(expenseAccounts[0].userId) : "",
        );
      } else if (!selectedExpenseAccountId && expenseAccounts.length) {
        setSelectedExpenseAccountId(String(expenseAccounts[0].userId));
      }
    }
  }, [
    isAdmin,
    entryMode,
    activeUsers,
    expenseAccounts,
    selectedEntryUserId,
    selectedExpenseAccountId,
    hasExpenseAccounts,
    hasAdminOverride,
  ]);

  useEffect(() => {
    resetEntryInputs();
    resetExpenseDetailsInput();
    setActiveExpenseTime(null);
    setDisabledExpenses(false);
  }, [effectiveUserId, resetEntryInputs, resetExpenseDetailsInput]);

  useEffect(() => {
    if (!api || loading || !effectiveUserId) {
      setProspects([]);
      setTimeDetails([]);
      setTimeDetailsLoaded(false);
      return;
    }

    loadProspects();
    loadClients();
  }, [api, effectiveUserId, loading, loadProspects, loadClients]);

  useEffect(() => {
    if (!api || loading || !effectiveUserId || !rangeStart || !rangeEnd) return;
    const timer = setTimeout(() => loadTimeDetails(), 400);
    return () => clearTimeout(timer);
  }, [api, loading, effectiveUserId, rangeStart, rangeEnd, loadTimeDetails]);

  const canAddForTarget = !!effectiveUserId;
  const canOpenExpenseModal = !!effectiveUserId;

  const daysRemaining= rangeEnd ? Math.max(0, dayjs(rangeEnd).diff(dayjs(), 'day')) : 0;
  const showTimeDueBanner = Boolean(timeDueMetadata && !timeDueBannerDismissed);

  const visibleColumns: string[] = [
    'minmax(60px, 0.5fr)',  // Date — always shown
  ];
  if (showClient) visibleColumns.push('minmax(80px, 1fr)');
  if (showProspect) visibleColumns.push('minmax(80px, 1fr)');
  if (showLegal) visibleColumns.push('minmax(100px, 1.3fr)');
  if (showJob) visibleColumns.push('minmax(80px, 0.8fr)');
  visibleColumns.push('minmax(50px, 0.4fr)');  // Days — always shown
  visibleColumns.push('100px');  // Actions — always shown, fixed width for up to 3 buttons
  const gridColumns = visibleColumns.join(' ');
  const gridStyle = { gridTemplateColumns: gridColumns } as React.CSSProperties;

  return (
    <>
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        authenticationRequest={authRequest}
      >
        <>
          <div className="main-articles entry-articles">
            <div className={`entry-type ${styles['time-entry-shell']}`}>
              {showTimeDueBanner && timeDueMetadata && (
                <div className={styles['time-entry-due-banner']} role="status" data-testid="time-due-banner">
                  <div>
                    <div className={styles['time-entry-due-banner-title']}>Time is due today</div>
                    <div className={styles['time-entry-due-banner-copy']}>
                      {timeDueMetadata.clients?.map((client) => (
                        <span key={`${client.clientId || client.clientName}-${client.periodStart}-${client.periodEnd}`}>
                          {client.clientName}: {client.periodStart} - {client.periodEnd}
                        </span>
                      ))}
                    </div>
                  </div>
                  <button
                    type="button"
                    className={styles['time-entry-due-banner-dismiss']}
                    onClick={() => setTimeDueBannerDismissed(true)}
                    aria-label="Dismiss time due reminder"
                  >
                    Dismiss
                  </button>
                </div>
              )}
              <form action="#" method="POST" style={{ display: 'flex', flexDirection: 'column', gap: 'inherit' }}>
                <div className={styles['time-entry-card']} data-testid="billing-period-card">
                  <div className={styles['time-entry-card-header']}>
                    <h2 className={styles['time-entry-card-header-title']}>Billing Period</h2>
                  </div>
                  <div className={styles['time-entry-card-body']}>
                  {isAdmin && (hasAdminOverride || hasExpenseAccounts) && (
                    <div className={styles['time-entry-context-row']}>
                      <div className={styles['time-entry-context-tabs']}>
                        <button
                          type="button"
                          className={`${styles['time-entry-pill']}${
                            entryMode === "self" ? ` ${styles['time-entry-pill--active']}` : ""
                          }`}
                          onClick={() => setEntryMode("self")}
                          aria-pressed={entryMode === "self"}
                        >
                          My Time
                        </button>

                        {hasAdminOverride && (
                        <button
                          type="button"
                          className={`${styles['time-entry-pill']}${
                            entryMode === "user" ? ` ${styles['time-entry-pill--active']}` : ""
                          }`}
                          onClick={() => setEntryMode("user")}
                          aria-pressed={entryMode === "user"}
                        >
                          User
                        </button>
                        )}

                        {hasExpenseAccounts && (
                        <button
                          type="button"
                          className={`${styles['time-entry-pill']}${
                            entryMode === "expense" ? ` ${styles['time-entry-pill--active']}` : ""
                          }`}
                          onClick={() => setEntryMode("expense")}
                          aria-pressed={entryMode === "expense"}
                        >
                          Company Expense
                        </button>
                        )}
                      </div>

                      {entryMode === "user" && (
                        <div className={styles['time-entry-context-picker']}>
                          <label htmlFor="entryUserSelect">Select User</label>
                          <SearchableSelect
                            id="entryUserSelect"
                            className={styles['time-entry-control']}
                            options={activeUsers.map((u) => ({ value: Number(u.userId), label: getStandardUserDisplayName(u) }))}
                            value={selectedEntryUserId ? Number(selectedEntryUserId) : null}
                            onChange={(val) => setSelectedEntryUserId(val ? String(val) : "")}
                            placeholder={activeUsers.length ? "Search users..." : "No active users"}
                            disabled={!activeUsers.length}
                          />
                        </div>
                      )}

                      {entryMode === "expense" && (
                        <div className={styles['time-entry-context-picker']}>
                          <label htmlFor="entryExpenseSelect">
                            Select Company Expense Account
                          </label>
                          <SearchableSelect
                            id="entryExpenseSelect"
                            className={styles['time-entry-control']}
                            options={expenseAccounts.map((u) => ({ value: Number(u.userId), label: getExpenseAccountDisplayName(u) }))}
                            value={selectedExpenseAccountId ? Number(selectedExpenseAccountId) : null}
                            onChange={(val) => setSelectedExpenseAccountId(val ? String(val) : "")}
                            placeholder={expenseAccounts.length ? "Search expense accounts..." : "No active expense accounts"}
                            disabled={!expenseAccounts.length}
                          />
                        </div>
                      )}

                      {!!effectiveContextLabel && entryMode !== "self" && (
                        <div className={styles['time-entry-context-summary']}>
                          Submitting as{" "}
                          <strong>{effectiveContextLabel}</strong>
                        </div>
                      )}
                    </div>
                  )}

                  <div className={styles['time-entry-period-group']}>
                    <div className={styles['time-entry-period-row']}>
                      <button
                        type="button"
                        className={styles['time-entry-period-arrow']}
                        onClick={() => shiftDisplayedPeriod(-1)}
                        onMouseUp={handleButtonPointerRelease}
                        onTouchEnd={handleButtonPointerRelease}
                        aria-label="Previous billing period"
                      >
                        <ArrowLeftIcon />
                      </button>

                      <div className={styles['time-entry-period-picker']}>
                        <TimeEntryDateRangeField
                          rangeStart={rangeStart}
                          rangeEnd={rangeEnd}
                          onChange={({ from, to }: any) => {
                            setRangeStart(from);
                            setRangeEnd(to);
                          }}
                        />
                      </div>

                      <button
                        type="button"
                        className={styles['time-entry-period-arrow']}
                        onClick={() => shiftDisplayedPeriod(1)}
                        onMouseUp={handleButtonPointerRelease}
                        onTouchEnd={handleButtonPointerRelease}
                        aria-label="Next billing period"
                      >
                        <ArrowRightIcon />
                      </button>
                    </div>
                    <div className={styles['time-entry-period-hint']}>Semi-monthly · {daysRemaining} days remaining</div>
                  </div>
                  </div>
                </div>

                <div className={styles['time-entry-card']} data-testid="new-entry-card">
                  <div className={styles['time-entry-card-header']}>
                    <h2 className={styles['time-entry-card-header-title']}>New Entry</h2>
                  </div>
                  <div className={styles['time-entry-card-body']}>
                    <div className={styles['time-entry-composer-row1']}>
                      <div className={styles['time-entry-field']}>
                        <label htmlFor="dateRangePicker">Date</label>
                        <select
                          id="dateRangePicker"
                          className={`${styles['time-entry-control']}${isAddEntryFieldMissing("date") ? ` ${styles['time-entry-control--invalid']}` : ''}`}
                          value={selectedDate}
                          onChange={(e) => setSelectedDate(e.target.value)}
                          aria-invalid={isAddEntryFieldMissing("date")}
                          aria-describedby={isAddEntryFieldMissing("date") ? ADD_ENTRY_VALIDATION_ID : undefined}
                        >
                          <option value="">Select Date</option>
                          {dateOptions.map((date) => (
                            <option key={date} value={date}>
                              {dayjs(date).format("MMM D")}
                            </option>
                          ))}
                        </select>
                      </div>

                      {showClient && (
                      <div className={styles['time-entry-field']}>
                        <label htmlFor="entryClient">Client</label>
                        <SearchableSelect
                          id="entryClient"
                          className={`${styles['time-entry-control']}${isAddEntryFieldMissing("client") ? ` ${styles['time-entry-control--invalid']}` : ''}`}
                          options={clients.map((c: any) => ({ value: c.clientId, label: c.businessName }))}
                          value={selectedClient ? Number(selectedClient) : null}
                          onChange={(val) => {
                            setSelectedClient(val ? String(val) : "");
                            setSelectedProspect("");
                            setSelectedLegal("");
                            setLegals([]);
                          }}
                          placeholder="Search clients..."
                          disabled={!clients.length}
                          aria-invalid={isAddEntryFieldMissing("client")}
                          aria-describedby={isAddEntryFieldMissing("client") ? ADD_ENTRY_VALIDATION_ID : undefined}
                          aria-errormessage={isAddEntryFieldMissing("client") ? ADD_ENTRY_VALIDATION_ID : undefined}
                        />
                      </div>
                      )}

                      {showProspect && (
                      <div className={styles['time-entry-field']}>
                        <label htmlFor="entryProspect">Prospect</label>
                        <SearchableSelect
                          id="entryProspect"
                          className={`${styles['time-entry-control']}${isAddEntryFieldMissing("prospect") ? ` ${styles['time-entry-control--invalid']}` : ''}`}
                          options={showClient
                            ? (selectedClient
                                ? prospects
                                    .filter((p: any) => String(p.clientId) === String(selectedClient))
                                    .map((p) => ({ value: p.projectId, label: p.projectName }))
                                : [])
                            : prospects.map((p) => ({ value: p.projectId, label: p.projectName }))}
                          value={selectedProspect ? Number(selectedProspect) : null}
                          onChange={(val) => {
                            const projectId = val ? String(val) : "";
                            setSelectedProspect(projectId);
                            if (!showClient && projectId) {
                              const prospectData = prospects.find((p: any) => String(p.projectId) === projectId);
                              if (prospectData) setSelectedClient(String(prospectData.clientId));
                            }
                            loadLegals(projectId);
                          }}
                          placeholder={showClient && !selectedClient ? "Select Client first" : "Search prospects..."}
                          disabled={(showClient && !selectedClient) || !prospects.length}
                          aria-invalid={isAddEntryFieldMissing("prospect")}
                          aria-describedby={isAddEntryFieldMissing("prospect") ? ADD_ENTRY_VALIDATION_ID : undefined}
                          aria-errormessage={isAddEntryFieldMissing("prospect") ? ADD_ENTRY_VALIDATION_ID : undefined}
                        />
                      </div>
                      )}

                      {showLegal && (
                      <div className={styles['time-entry-field']}>
                        <label htmlFor="entryLegal">Legal</label>
                        <SearchableSelect
                          id="entryLegal"
                          className={`${styles['time-entry-control']}${isAddEntryFieldMissing("legal") ? ` ${styles['time-entry-control--invalid']}` : ''}`}
                          options={selectedProspect
                            ? legals.map((l) => ({ value: l.id, label: formatLegalLabel(l) }))
                            : []}
                          value={selectedLegal ? Number(selectedLegal) : null}
                          onChange={(val) => setSelectedLegal(val ? String(val) : "")}
                          placeholder={!selectedProspect ? "Select Prospect first" : legalsLoading ? "Loading…" : "Search legals..."}
                          disabled={!selectedProspect || legalsLoading}
                          aria-invalid={isAddEntryFieldMissing("legal")}
                          aria-describedby={isAddEntryFieldMissing("legal") ? ADD_ENTRY_VALIDATION_ID : undefined}
                          aria-errormessage={isAddEntryFieldMissing("legal") ? ADD_ENTRY_VALIDATION_ID : undefined}
                        />
                      </div>
                      )}
                    </div>

                    <div className={styles['time-entry-composer-row2']}>
                      {showJob && (
                      <div className={styles['time-entry-field']}>
                        <label htmlFor="entryJob">Job</label>
                        <select
                          id="entryJob"
                          className={`${styles['time-entry-control']}${isAddEntryFieldMissing("job") ? ` ${styles['time-entry-control--invalid']}` : ''}`}
                          value={selectedJob}
                          disabled={!jobs.length}
                          onChange={(e) => setSelectedJob(e.target.value)}
                          aria-invalid={isAddEntryFieldMissing("job")}
                          aria-describedby={isAddEntryFieldMissing("job") ? ADD_ENTRY_VALIDATION_ID : undefined}
                        >
                          <option value="">Select Job</option>
                          {jobs.map((j) => (
                            <option key={j.id} value={j.id}>
                              {j.jobName}
                            </option>
                          ))}
                        </select>
                      </div>
                      )}

                      <div className={styles['time-entry-field']}>
                        <label htmlFor="entryDays">Days</label>
                        <select
                          id="entryDays"
                          className={`${styles['time-entry-control']}${isAddEntryFieldMissing("days") ? ` ${styles['time-entry-control--invalid']}` : ''}`}
                          value={selectedDays}
                          onChange={(e) => setSelectedDays(e.target.value)}
                          aria-invalid={isAddEntryFieldMissing("days")}
                          aria-describedby={isAddEntryFieldMissing("days") ? ADD_ENTRY_VALIDATION_ID : undefined}
                        >
                          <option value="">Select Days</option>
                          {ADD_ENTRY_DAY_OPTIONS.map((day) => (
                            <option key={day} value={day}>
                              {day}
                            </option>
                          ))}
                        </select>
                      </div>

                      {showDescription && (
                      <div className={styles['time-entry-field']}>
                        <label htmlFor="entryDescription">Description</label>
                        <input
                          type="text"
                          id="entryDescription"
                          className={styles['time-entry-control']}
                          placeholder="Enter description…"
                          value={selectedDescription}
                          onChange={(e) => setSelectedDescription(e.target.value)}
                        />
                      </div>
                      )}

                      <div className={styles['time-entry-composer__actions']}>
                        {hasExpenseEntry && (
                        <button
                          type="button"
                          className={styles['time-entry-expense-btn']}
                          data-bs-toggle={
                            canOpenExpenseModal ? "modal" : undefined
                          }
                          data-bs-target={
                            canOpenExpenseModal ? "#expenseModal" : undefined
                          }
                          onClick={() =>
                            canOpenExpenseModal && getExpenseDetails(null)
                          }
                          onMouseUp={handleButtonPointerRelease}
                          onTouchEnd={handleButtonPointerRelease}
                          disabled={!canOpenExpenseModal}
                          aria-label="Open expenses"
                          title="Expenses"
                        >
                          <ReceiptIcon />
                        </button>
                        )}

                        <button
                          type="button"
                          className={styles['time-entry-primary-btn']}
                          onClick={addEntry}
                          onMouseUp={handleButtonPointerRelease}
                          onTouchEnd={handleButtonPointerRelease}
                          disabled={!canAddForTarget}
                        >
                          <PlusIcon />
                          Add Entry
                        </button>
                      </div>
                    </div>
                    {showAddEntryValidation && missingAddEntryFields.length > 0 && (
                      <div
                        id={ADD_ENTRY_VALIDATION_ID}
                        className={styles['time-entry-validation-alert']}
                        role="alert"
                      >
                        <div className={styles['time-entry-validation-alert-title']}>
                          Complete the required fields before adding an entry.
                        </div>
                        <ul className={styles['time-entry-validation-list']}>
                          {missingAddEntryFields.map((field) => (
                            <li key={field.key}>{field.label}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                {(rangeStart && rangeEnd) && (
                  <div className={`${styles['time-entry-card']}${!timeDetailsLoaded ? ` ${styles['time-entry-card--loading']}` : ''}`} data-testid="submitted-entries-card">
                    <div className={styles['time-entry-card-header']}>
                      <h2 className={styles['time-entry-card-header-title']}>
                        Submitted Entries
                        {timeDetailsLoaded && timeDetails.length > 0 && (
                          <>
                            <span className={styles['time-entry-count-badge']}>
                              {timeDetails.length} {timeDetails.length === 1 ? 'entry' : 'entries'}
                            </span>
                            {hasApprovals && (
                            <>
                            <span className={`${styles['time-entry-count-badge']} ${styles['time-entry-count-badge--pending']}`}>
                              {timeDetails.filter(d => d.approvalStatus !== 'rejected' && d.approvalStatus !== 'approved' && Number(d.invoiceId || 0) === 0).length} pending
                            </span>
                            <span className={`${styles['time-entry-count-badge']} ${styles['time-entry-count-badge--invoiced']}`}>
                              {timeDetails.filter(d => d.approvalStatus !== 'rejected' && Number(d.invoiceId || 0) > 0).length} invoiced
                            </span>
                            {timeDetails.filter(d => d.approvalStatus === 'rejected').length > 0 && (
                              <span className={`${styles['time-entry-count-badge']} ${styles['time-entry-count-badge--denied']}`}>
                                {timeDetails.filter(d => d.approvalStatus === 'rejected').length} rejected
                              </span>
                            )}
                            {timeDetails.filter(d => d.approvalStatus === 'approved' && Number(d.invoiceId || 0) === 0).length > 0 && (
                              <span className={`${styles['time-entry-count-badge']} ${styles['time-entry-count-badge--approved']}`}>
                                {timeDetails.filter(d => d.approvalStatus === 'approved' && Number(d.invoiceId || 0) === 0).length} approved
                              </span>
                            )}
                            </>
                            )}
                          </>
                        )}
                      </h2>
                    </div>
                    <div className={styles['time-entry-card-body-flush']}>

                    {!timeDetailsLoaded ? (
                      <div className={styles['time-entry-loading-state']}>
                        Retrieving time entries for this billing period…
                      </div>
                    ) : timeDetails.length === 0 ? (
                      <div className={styles['time-entry-empty-state']}>
                        <svg className={styles['time-entry-empty-icon']} width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden="true">
                          <rect x="8" y="10" width="32" height="30" rx="4" stroke="#d1d5db" strokeWidth="2" />
                          <path d="M8 18h32" stroke="#d1d5db" strokeWidth="2" />
                          <path d="M16 6v8M32 6v8" stroke="#d1d5db" strokeWidth="2" strokeLinecap="round" />
                          <circle cx="24" cy="30" r="4" stroke="#d1d5db" strokeWidth="2" />
                        </svg>
                        <p className={styles['time-entry-empty-title']}>No entries yet</p>
                        <p className={styles['time-entry-empty-subtitle']}>Use the form above to add your first time entry for this billing period.</p>
                      </div>
                    ) : (
                      <>
                        <div className={styles['time-entry-grid-header']} style={gridStyle}>
                          <span>Date</span>
                          {showClient && <span>Client</span>}
                          {showProspect && <span>Prospect</span>}
                          {showLegal && <span>Legal</span>}
                          {showJob && <span>Job</span>}
                          <span>Days</span>
                          <span>Actions</span>
                        </div>

                        {timeDetails.map((d) => {
                          const rowLegals =
                            legalsByProject[Number(d.projectId)] || [];
                          const selLegalStr =
                            d.projectDescriptionId != null
                              ? String(d.projectDescriptionId)
                              : "";
                          const isInvoiced = Number(d.invoiceId || 0) > 0;
                          const isDenied = d.approvalStatus === 'rejected';
                          const isApproved = d.approvalStatus === 'approved';
                          const isLocked = isInvoiced;
                          const isEditing = editingRowId === d.timeId && !isLocked;
                          const prospectItem = prospects.find((p: any) => String(p.projectId) === String(d.projectId));
                          const clientItem = clients.find((c: any) => String(c.clientId) === String(prospectItem?.clientId));
                          const clientDisplayName = clientItem?.businessName ?? '';
                          const prospectName = prospectItem?.projectName ?? '';
                          const legalObj = rowLegals.find((l: any) => String(l.id) === selLegalStr);
                          const legalName = legalObj ? formatLegalLabel(legalObj) : String(d.projectDescriptionName ?? '');
                          const jobName = jobs.find((j: any) => String(j.id) === String(d.jobTypeId ?? d.jobTypeID))?.jobName ?? '';
                          const dateDisplay = dayjs(d.workDate).format("MMM D");
                          const dateValue = dayjs(d.workDate).format("YYYY-MM-DD");
                          const jobTypeValue = String(d.jobTypeId ?? d.jobTypeID ?? "");

                          const isExpanded = expandedRowId === d.timeId;

                          const statusClass = !hasApprovals
                            ? ''
                            : isDenied
                            ? styles['time-entry-row-denied']
                            : isInvoiced
                              ? styles['time-entry-row-invoiced']
                              : isApproved
                                ? styles['time-entry-row-approved']
                                : styles['time-entry-row-pending'];

                          return (
                            <React.Fragment key={d.timeId}>
                              <div
                                className={`${styles['time-entry-entry-wrapper']} ${statusClass}${highlightTimeId === d.timeId ? ` ${styles['time-entry-row-highlight']}` : ''}`}
                                data-testid={`time-entry-${d.timeId}`}
                                onClick={(e) => {
                                  if (isEditing) return;
                                  if ((e.target as HTMLElement).closest('button')) return;
                                  if ((e.target as HTMLElement).closest('select')) return;
                                  if ((e.target as HTMLElement).closest('input')) return;
                                  const newExpandedId = isExpanded ? null : d.timeId!;
                                  setExpandedRowId(newExpandedId);
                                  if (newExpandedId && d.totalSteps && d.totalSteps > 0) {
                                    fetchApprovalChain(newExpandedId);
                                  }
                                }}
                                style={{ cursor: isEditing ? undefined : 'pointer' }}
                              >
                                {isEditing ? (
                                  <>
                                  <div className={`${styles['submitted-time-row-desktop']} ${styles['time-entry-grid-data']}`} style={gridStyle}>
                                    <select
                                      className={`${styles['time-entry-control']} ${styles['time-entry-control--dense']}`}
                                      value={dateValue}
                                      onChange={(e) => handleTimeDetailChange(d.timeId, "workDate", e.target.value)}
                                    >
                                      <option value="">Select Date</option>
                                      {dateOptions.map((date) => (
                                        <option key={date} value={date}>{dayjs(date).format("MMM D")}</option>
                                      ))}
                                    </select>
                                    {showClient && (
                                    <SearchableSelect
                                      className={`${styles['time-entry-control']} ${styles['time-entry-control--dense']}`}
                                      options={clients.map((c: any) => ({ value: c.clientId, label: c.businessName }))}
                                      value={prospectItem?.clientId ? Number(prospectItem.clientId) : null}
                                      onChange={(val) => {
                                        const newClientId = val ? String(val) : "";
                                        const firstProspect = prospects.find((p: any) => String(p.clientId) === newClientId);
                                        if (firstProspect) {
                                          handleRowProspectChange(d.timeId, firstProspect.projectId, { userInitiated: true });
                                        }
                                      }}
                                      placeholder="Search clients..."
                                    />
                                    )}
                                    {showProspect && (
                                    <SearchableSelect
                                      className={`${styles['time-entry-control']} ${styles['time-entry-control--dense']}`}
                                      options={showClient
                                        ? prospects
                                            .filter((p: any) => !prospectItem?.clientId || String(p.clientId) === String(prospectItem.clientId))
                                            .map((p) => ({ value: p.projectId, label: p.projectName }))
                                        : prospects.map((p) => ({ value: p.projectId, label: p.projectName }))}
                                      value={d.projectId ? Number(d.projectId) : null}
                                      onChange={async (val) => {
                                        const newPid = val ?? 0;
                                        await handleRowProspectChange(d.timeId, newPid, { userInitiated: true });
                                      }}
                                      placeholder="Search prospects..."
                                    />
                                    )}
                                    {showLegal && (
                                    <SearchableSelect
                                      className={`${styles['time-entry-control']} ${styles['time-entry-control--dense']}`}
                                      options={rowLegals.length
                                        ? rowLegals.map((l: any) => ({ value: l.id, label: formatLegalLabel(l) }))
                                        : selLegalStr
                                          ? [{ value: Number(selLegalStr), label: legalName || "Loading…" }]
                                          : []}
                                      value={selLegalStr ? Number(selLegalStr) : null}
                                      onChange={(val) => handleTimeDetailChange(d.timeId, "projectDescriptionId", val)}
                                      placeholder={!rowLegals.length ? "Loading…" : "Search legals..."}
                                    />
                                    )}
                                    {showJob && (
                                    <select
                                      className={`${styles['time-entry-control']} ${styles['time-entry-control--dense']}`}
                                      value={jobTypeValue}
                                      aria-label="Select job type"
                                      onChange={(e) => handleTimeDetailChange(d.timeId, "jobTypeId", Number(e.target.value))}
                                    >
                                      <option value="">Select Job</option>
                                      {jobs.map((j) => (
                                        <option key={j.id} value={String(j.id)}>{j.jobName}</option>
                                      ))}
                                    </select>
                                    )}
                                    <select
                                      className={`${styles['time-entry-control']} ${styles['time-entry-control--dense']}`}
                                      value={String(d.hours ?? "")}
                                      onChange={(e) => handleTimeDetailChange(d.timeId, "hours", Number(e.target.value))}
                                    >
                                      <option value="">Days</option>
                                      {ROW_DAY_OPTIONS.map((day) => (
                                        <option key={day} value={String(day)}>{day}</option>
                                      ))}
                                    </select>
                                    <span className={styles['submitted-time-row__actions']}>
                                      <button
                                        type="button"
                                        className={styles['time-entry-icon-btn']}
                                        onClick={async () => {
                                          await saveTimeDetail(d);
                                          setEditingRowId(null);
                                        }}
                                        onMouseUp={handleButtonPointerRelease}
                                        onTouchEnd={handleButtonPointerRelease}
                                        aria-label="Save row"
                                        title="Save changes"
                                        disabled={!!savingRows[d.timeId!]}
                                        data-testid={`time-entry-save-${d.timeId}`}
                                      >
                                        <SaveIcon />
                                      </button>
                                      <button
                                        type="button"
                                        className={styles['time-entry-icon-btn']}
                                        onClick={() => setEditingRowId(null)}
                                        onMouseUp={handleButtonPointerRelease}
                                        onTouchEnd={handleButtonPointerRelease}
                                        aria-label="Cancel edit"
                                        title="Cancel"
                                      >
                                        ×
                                      </button>
                                    </span>
                                  </div>
                                  {showDescription && (
                                  <div style={{ padding: '4px 16px 8px' }}>
                                    <input
                                      type="text"
                                      className={`${styles['time-entry-control']} ${styles['time-entry-control--dense']}`}
                                      placeholder="Description"
                                      value={d.description ?? ""}
                                      onChange={(e) => handleTimeDetailChange(d.timeId, "description", e.target.value)}
                                      data-testid={`entry-description-${d.timeId}`}
                                      style={{ width: '100%' }}
                                    />
                                  </div>
                                  )}
                                  </>
                                ) : (
                                <div
                                  className={`${styles['submitted-time-row-desktop']} ${styles['time-entry-grid-data']}${isExpanded ? ` ${styles['time-entry-grid-data--expanded']}` : ''}`}
                                  style={gridStyle}
                                >
                                  <span>{dateDisplay}</span>
                                  {showClient && <span>{clientDisplayName}</span>}
                                  {showProspect && <span>{prospectName}</span>}
                                  {showLegal && <span>{legalName}</span>}
                                  {showJob && <span>{jobName}</span>}
                                  <span>{d.hours}</span>
                                  <span className={styles['submitted-time-row__actions']}>
                                    {hasExpenseEntry && (
                                      <button
                                        type="button"
                                        className={styles['time-entry-icon-btn']}
                                        data-bs-toggle="modal"
                                        data-bs-target="#expenseModal"
                                        onClick={() => getExpenseDetails(d)}
                                        onMouseUp={handleButtonPointerRelease}
                                        onTouchEnd={handleButtonPointerRelease}
                                        aria-label={isLocked ? "View expenses" : "Expenses"}
                                        title={isLocked ? "View invoiced expenses and receipts" : "Expenses"}
                                      >
                                        <ReceiptIcon />
                                        {rowsWithExpenses.has(Number(d.timeId)) && (
                                          <span className={styles['time-entry-expense-dot']} />
                                        )}
                                      </button>
                                    )}

                                    {!isLocked && (
                                      <button
                                        type="button"
                                        className={styles['time-entry-icon-btn']}
                                        onClick={() => setEditingRowId(d.timeId!)}
                                        onMouseUp={handleButtonPointerRelease}
                                        onTouchEnd={handleButtonPointerRelease}
                                        aria-label="Edit row"
                                        title="Edit entry"
                                        data-testid={`time-entry-edit-${d.timeId}`}
                                      >
                                        <EditIcon />
                                      </button>
                                    )}

                                    {!isLocked && (
                                      <button
                                        type="button"
                                        className={`${styles['time-entry-icon-btn']} ${styles['time-entry-icon-btn--danger']}`}
                                        onClick={() => deleteTimeDetail(d.timeId)}
                                        onMouseUp={handleButtonPointerRelease}
                                        onTouchEnd={handleButtonPointerRelease}
                                        aria-label="Delete row"
                                        title="Delete row"
                                        data-testid={`time-entry-delete-${d.timeId}`}
                                      >
                                        <TrashIcon />
                                      </button>
                                    )}
                                  </span>
                                </div>
                                )}
                                {showDescription && d.description && (
                                  <div className={styles['time-entry-description-line']}>{d.description}</div>
                                )}
                                {hasApprovals && !isInvoiced && (d.totalSteps ?? 0) > 0 && (
                                  <div className={styles['time-entry-approval-progress']}>
                                    <span className={styles['time-entry-approval-steps']}>
                                      {d.approvalStatus === 'approved'
                                        ? `✓ Fully approved (${d.totalSteps}/${d.totalSteps})`
                                        : `Approvals ${d.completedSteps ?? 0} of ${d.totalSteps}`}
                                    </span>
                                    {isExpanded && approvalChains[d.timeId!] && (
                                      <div className={styles['time-entry-approval-chain']}>
                                        {approvalChains[d.timeId!].steps.map((step, i) => (
                                          <div key={step.id} className={styles['time-entry-approval-step']}>
                                            <span className={`${styles['time-entry-approval-dot']} ${
                                              step.status === 'approved'
                                                ? styles['time-entry-approval-dot--approved']
                                                : step.status === 'rejected'
                                                  ? styles['time-entry-approval-dot--denied']
                                                  : styles['time-entry-approval-dot--pending']
                                            }`} />
                                            {i < approvalChains[d.timeId!].steps.length - 1 && (
                                              <span className={`${styles['time-entry-approval-connector']} ${
                                                step.status === 'approved' ? styles['time-entry-approval-connector--done'] : ''
                                              }`} />
                                            )}
                                            <span className={styles['time-entry-approval-label']}>
                                              {step.approverName}
                                              <span className={styles['time-entry-approval-type']}>{step.approverType}</span>
                                              {step.status === 'approved' && step.respondedAt && (
                                                <span className={styles['time-entry-approval-date']}>
                                                  {dayjs(step.respondedAt).format('MMM D')}
                                                </span>
                                              )}
                                              {step.status === 'rejected' && step.comments && (
                                                <span className={styles['time-entry-approval-comment']}>
                                                  {step.respondedAt ? `${dayjs(step.respondedAt).format('MMM D')} - ` : ''}"{step.comments}"
                                                </span>
                                              )}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>

                              <div
                                className={`${styles['submitted-time-row-card']}${
                                  isLocked
                                    ? ` ${styles['submitted-time-row-card--locked']}`
                                    : ""
                                }${highlightTimeId === d.timeId ? ` ${styles['time-entry-row-highlight']}` : ''}`}
                              >
                                <div className={styles['submitted-time-row-card__header']}>
                                  <span className={styles['time-entry-cell-value']} style={{ fontSize: '14px', fontWeight: 600 }}>{dateDisplay}</span>
                                  <span style={{ fontSize: '11px', color: isDenied ? '#ef4444' : isInvoiced ? '#22c55e' : isApproved ? '#f69e21' : '#397ffb', fontWeight: 600 }}>
                                    {isDenied ? 'Rejected' : isInvoiced ? 'Invoiced' : isApproved ? 'Approved' : 'Pending'}
                                  </span>
                                </div>
                                <div className={styles['submitted-time-row-card__body']}>
                                  {showClient && (
                                  <div className={styles['time-entry-cell']}>
                                    <span className={styles['time-entry-cell-label']}>Client</span>
                                    <span className={styles['time-entry-cell-value']}>{clientDisplayName}</span>
                                  </div>
                                  )}
                                  {showProspect && (
                                  <div className={styles['time-entry-cell']}>
                                    <span className={styles['time-entry-cell-label']}>Prospect</span>
                                    <span className={styles['time-entry-cell-value']}>{prospectName}</span>
                                  </div>
                                  )}
                                  {showLegal && (
                                  <div className={styles['time-entry-cell']}>
                                    <span className={styles['time-entry-cell-label']}>Legal</span>
                                    <span className={styles['time-entry-cell-value']}>{legalName}</span>
                                  </div>
                                  )}
                                  {showJob && (
                                  <div className={styles['time-entry-cell']}>
                                    <span className={styles['time-entry-cell-label']}>Job</span>
                                    <span className={styles['time-entry-cell-value']}>{jobName}</span>
                                  </div>
                                  )}
                                  <div className={styles['time-entry-cell']}>
                                    <span className={styles['time-entry-cell-label']}>Days</span>
                                    <span className={styles['time-entry-cell-value']}>{d.hours}</span>
                                  </div>
                                </div>
                                {showDescription && d.description && (
                                  <div className={styles['time-entry-description-line']}>{d.description}</div>
                                )}
                                {hasApprovals && !isInvoiced && (d.totalSteps ?? 0) > 0 && (
                                  <div className={styles['time-entry-approval-progress']}>
                                    <span className={styles['time-entry-approval-steps']}>
                                      {d.approvalStatus === 'approved'
                                        ? `✓ Fully approved (${d.totalSteps}/${d.totalSteps})`
                                        : `Approvals ${d.completedSteps ?? 0} of ${d.totalSteps}`}
                                    </span>
                                  </div>
                                )}
                                <div className={styles['submitted-time-row-card__actions']}>
                                  {hasExpenseEntry && (
                                    <button
                                      type="button"
                                      className={styles['time-entry-icon-btn']}
                                      data-bs-toggle="modal"
                                      data-bs-target="#expenseModal"
                                      onClick={() => getExpenseDetails(d)}
                                      onMouseUp={handleButtonPointerRelease}
                                      onTouchEnd={handleButtonPointerRelease}
                                      aria-label={isLocked ? "View expenses" : "Expenses"}
                                      title={isLocked ? "View invoiced expenses and receipts" : "Expenses"}
                                    >
                                      <ReceiptIcon />
                                      {rowsWithExpenses.has(Number(d.timeId)) && (
                                        <span className={styles['time-entry-expense-dot']} />
                                      )}
                                    </button>
                                  )}
                                  {!isLocked && (
                                    <button
                                      type="button"
                                      className={styles['time-entry-icon-btn']}
                                      onClick={() => setEditingRowId(d.timeId!)}
                                      onMouseUp={handleButtonPointerRelease}
                                      onTouchEnd={handleButtonPointerRelease}
                                      aria-label="Edit row"
                                      title="Edit entry"
                                      data-testid={`time-entry-edit-${d.timeId}`}
                                    >
                                      <EditIcon />
                                    </button>
                                  )}
                                  {!isLocked && (
                                    <button
                                      type="button"
                                      className={`${styles['time-entry-icon-btn']} ${styles['time-entry-icon-btn--danger']}`}
                                      onClick={() => deleteTimeDetail(d.timeId)}
                                      onMouseUp={handleButtonPointerRelease}
                                      onTouchEnd={handleButtonPointerRelease}
                                      aria-label="Delete row"
                                      title="Delete row"
                                      data-testid={`time-entry-delete-${d.timeId}`}
                                    >
                                      <TrashIcon />
                                    </button>
                                  )}
                                </div>
                              </div>
                            </React.Fragment>
                          );
                        })}
                      </>
                    )}
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>

          <FeatureGate feature="expense_entry">
          <div id="expenseModalWrapper" role="dialog" aria-modal="true">
            <div
              className="modal fade"
              id="expenseModal"
              tabIndex={-1}
              aria-labelledby="expenseModalLabel"
              aria-hidden="true"
              data-bs-backdrop="static"
            >
              <div className="modal-dialog modal-dialog-centered">
                <div className={`modal-content ${styles['expense-modal-content']}`}>
                  <form className={styles['expense-modal-form']} onSubmit={(e) => e.preventDefault()}>
                    <div className={styles['expense-modal-hdr']}>
                      <h3 id="expenseModalLabel">Expenses</h3>
                    </div>

                    <div key={expenseOpenKey} className={styles['expense-modal-body']}>
                    {disabledExpenses && (
                      <div className={styles['expense-modal-note']}>
                        This entry is already on an invoice. Expenses and
                        receipts are available to review but cannot be edited.
                      </div>
                    )}

                    <div className={styles['expense-fields']}>
                      {expenseTypes.length > 0 ? (
                        expenseDetails.lineItems.map((li) => {
                          const et = expenseTypes.find((t: any) => t.id === li.expenseTypeId);
                          if (!et) return null;
                          const isInteger = et.inputType === "integer";
                          return (
                            <div className={styles['expense-field-row']} key={et.id}>
                              <label className={styles['expense-field-label']} htmlFor={`expense-${et.id}`}>
                                {et.typeName}
                              </label>
                              <input
                                id={`expense-${et.id}`}
                                type="text"
                                inputMode={isInteger ? "numeric" : "decimal"}
                                className={styles['expense-field-input']}
                                placeholder={isInteger ? "0" : "0.00"}
                                value={li.amount}
                                onChange={(e) => handleExpenseAmountChange(et.id, e.target.value)}
                                disabled={disabledExpenses}
                              />
                            </div>
                          );
                        })
                      ) : (
                        <div style={{ color: '#63758f', fontSize: '13px', gridColumn: '1 / -1' }}>
                          Loading expense types...
                        </div>
                      )}
                    </div>

                    <FeatureGate feature="receipt_uploads">
                      <div
                        className={styles['expense-upload-zone']}
                        role="button"
                        tabIndex={disabledExpenses ? -1 : 0}
                        aria-disabled={disabledExpenses}
                        onClick={triggerReceiptBrowse}
                        onKeyDown={handleReceiptZoneKeyDown}
                        onDragEnter={handleReceiptDragEnter}
                        onDragOver={handleReceiptDragOver}
                        onDragLeave={handleReceiptDragLeave}
                        onDrop={handleReceiptDrop}
                        style={{
                          cursor: disabledExpenses ? "not-allowed" : "pointer",
                          opacity: disabledExpenses ? 0.65 : 1,
                          borderColor: isReceiptDragActive ? "#397ffb" : undefined,
                          background: isReceiptDragActive ? "#f8fbff" : undefined,
                        }}
                      >
                        <span>
                          {isReceiptDragActive
                            ? "Drop file to attach"
                            : "\ud83d\udcce Drop receipts here or click to upload"}
                        </span>
                        <span className={styles['expense-upload-hint']}>&middot; PDF, PNG, JPG up to 10 MB</span>

                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          data-max_length="20"
                          className={styles['hidden-input']}
                          onChange={addReceipt}
                          disabled={disabledExpenses}
                        />
                      </div>

                      {pendingReceipts.length > 0 && (
                        <ul className={styles['expense-receipt-list']}>
                          {pendingReceipts.map((r, index) => (
                            <li className={styles['expense-receipt-row']} key={r.pendingId} data-testid={`pending-receipt-${r.pendingId || index}`}>
                              <div className={styles['expense-receipt-row__meta']}>
                                <span className={styles['expense-receipt-row__name']} title={r.name}>
                                  {r.name}
                                </span>
                                <span className={styles['expense-receipt-row__tag']}>Pending</span>
                              </div>
                              {!disabledExpenses && (
                                <button
                                  type="button"
                                  className={styles['expense-receipt-row__remove']}
                                  onClick={() => removeReceipt(r)}
                                  aria-label={`Remove ${r.name}`}
                                >
                                  <TrashIcon />
                                </button>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}

                      {receiptDetails.length > 0 && (
                        <ul className={styles['expense-receipt-list']}>
                          {receiptDetails.map((r, index) => (
                            <li className={styles['expense-receipt-row']} key={`server-${r.receiptId}`} data-testid={`saved-receipt-${r.receiptId || index}`}>
                              <div className={styles['expense-receipt-row__meta']}>
                                <a
                                  href={r.location}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={r.name}
                                  download={r.name}
                                  className={styles['expense-receipt-row__link']}
                                >
                                  {r.name}
                                </a>
                              </div>
                              {!disabledExpenses && (
                                <button
                                  type="button"
                                  className={styles['expense-receipt-row__remove']}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    removeReceipt(r);
                                  }}
                                  aria-label={`Remove ${r.name}`}
                                >
                                  <TrashIcon />
                                </button>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </FeatureGate>
                    </div>

                    <div className={styles['expense-modal-footer']}>
                      <button
                        type="button"
                        className={styles['expense-cancel-btn']}
                        data-bs-dismiss="modal"
                      >
                        Close
                      </button>
                      {!disabledExpenses && (
                        <button
                          type="button"
                          className={styles['expense-save-btn']}
                          onClick={async () => {
                            await saveExpenseDetailsInput();
                            await uploadReceipts();
                          }}
                          data-bs-dismiss="modal"
                        >
                          Save Changes
                        </button>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
          </FeatureGate>
        </>
      </MsalAuthenticationTemplate>
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </>
  );
};


export default TimeEntry;
